// Portfel — calculation engine. Pure functions (no DOM), runs in the browser, in a Web Worker and in Node.
//
// Input: transactions in PTF format, instrument registry, quotes, NBP exchange rates.
// Model: daily timeline (calendar days, Europe/Warsaw time zone), FIFO lots per account,
// valuation in PLN, capital flows, tax liability (Belka), periodic TWR / XIRR.
//
// The model is built in two steps: buildModelData() (data only — can be built in a worker and sent
// via postMessage) and hydrateModel() (adds the idx/fx/fxTax/pPLN accessors and the cache).
// buildModel() = hydrateModel(buildModelData()).
import type {
  AccountSeries, AggregateResult, AllocationRow, BenchmarkResult, Day, DayRange, FeeEntry, Granularity, HoldingRow,
  HoldingsOptions, Horizon, IncomeEntry, Instrument, Lot, Model, ModelAccessors, ModelData, ModelInput, ModelOptions, OpenLotRow,
  PeriodicReturn, PeriodStats, Point, Purchase, PurchaseRow, RealizedEntry, RebalanceRow, ReconcileRow, RiskStats, TaxEntry, Tx,
  XferFlow, XirrFlow, YearRow, YearStats,
} from "./types";

export type * from "./types";

const DAY = 86400000;
const fmtDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Warsaw", year: "numeric", month: "2-digit", day: "2-digit" });

export function warsawDay(iso: string | number | Date): Day {
  return fmtDay.format(new Date(iso));
}
export function todayStr(): Day {
  return fmtDay.format(new Date());
}
const dayMs = (d: Day): number => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));
export function addDays(d: Day, n: number): Day {
  return new Date(dayMs(d) + n * DAY).toISOString().slice(0, 10);
}
export function diffDays(a: Day, b: Day): number {
  return Math.round((dayMs(b) - dayMs(a)) / DAY);
}
function addBusinessDays(d: Day, n: number): Day {
  let x = d;
  let k = n;
  while (k > 0) {
    x = addDays(x, 1);
    const wd = new Date(dayMs(x)).getUTCDay();
    if (wd !== 0 && wd !== 6) k--;
  }
  return x;
}
const num = (v: string | number | null | undefined): number => (v === undefined || v === null || v === "" ? 0 : +v);
const EPS = 1e-9;

// ------------------------------------------------------------------ XIRR
export function xirr(flows: readonly XirrFlow[]): number | null {
  // flows: [{t: ms, v: amount}] ; negative = contribution to the investment, positive = withdrawal / final value
  const f = flows.filter((x) => Math.abs(x.v) > 1e-9);
  if (f.length < 2) return null;
  if (!f.some((x) => x.v > 0) || !f.some((x) => x.v < 0)) return null;
  const t0 = f[0].t;
  const yrs = f.map((x) => (x.t - t0) / (365 * DAY));
  const npv = (r: number): number => f.reduce((s, x, i) => s + x.v / Math.pow(1 + r, yrs[i]), 0);
  let lo = -0.9999, hi = 10;
  let flo = npv(lo), fhi = npv(hi);
  if (flo * fhi > 0) {
    hi = 100; fhi = npv(hi);
    if (flo * fhi > 0) return null;
  }
  for (let k = 0; k < 200; k++) {
    const mid = (lo + hi) / 2;
    const fm = npv(mid);
    if (Math.abs(fm) < 1e-7) return mid;
    if (flo * fm < 0) { hi = mid; fhi = fm; } else { lo = mid; flo = fm; }
  }
  return (lo + hi) / 2;
}

// ------------------------------------------------------------------ price and exchange rate series
function ffillSeries(points: readonly (readonly [Day, number])[] | null | undefined, days: readonly Day[], start: Day): Float64Array {
  // points: [[YYYY-MM-DD, v]] sorted; returns a Float64Array (NaN before the first point)
  const n = days.length;
  const out = new Float64Array(n).fill(NaN);
  if (!points || !points.length) return out;
  let j = 0;
  let last = NaN;
  // points before the start of the axis -> the last of them as the starting value
  while (j < points.length && points[j][0] < start) { last = points[j][1]; j++; }
  for (let i = 0; i < n; i++) {
    const d = days[i];
    while (j < points.length && points[j][0] <= d) { last = points[j][1]; j++; }
    out[i] = last;
  }
  return out;
}
function bfillFirst(arr: Float64Array): Float64Array {
  let first = -1;
  for (let i = 0; i < arr.length; i++) if (!isNaN(arr[i])) { first = i; break; }
  if (first > 0) for (let i = 0; i < first; i++) arr[i] = arr[first];
  return arr;
}

// ------------------------------------------------------------------ accessors (shared by build and hydration)
type AccessorSource = Pick<ModelData, "N" | "firstDay" | "fxArr" | "priceArr" | "priceCcy" | "missingFx">;
function makeAccessors(d: AccessorSource): ModelAccessors {
  const { N, firstDay, fxArr, priceArr, priceCcy, missingFx } = d;
  const idx = (day: Day): number => Math.max(0, Math.min(N - 1, diffDays(firstDay, day)));
  // PLN exchange rate for 1 unit of currency
  const fx = (c: string, i: number): number => {
    const a = fxArr[c];
    if (!a) { missingFx.add(c); return NaN; }
    return a[i];
  };
  // NBP exchange rate from the last business day before day d (art. 11a of the Polish PIT act)
  const fxTax = (c: string, day: Day): number => {
    if (c === "PLN") return 1;
    const i = diffDays(firstDay, day) - 1;
    const a = fxArr[c];
    if (!a) { missingFx.add(c); return NaN; }
    return a[Math.max(0, Math.min(N - 1, i))];
  };
  const pPLN = (s: string, i: number): number => {
    const a = priceArr[s];
    if (!a) return NaN;
    return a[i] * fx(priceCcy[s], i);
  };
  return { idx, fx, fxTax, pPLN };
}

function splitLot(lot: Lot, q: number): Lot {
  const f = q / lot.qty;
  const part = { ...lot, qty: q, costPLN: lot.costPLN * f, costCur: lot.costCur * f };
  lot.qty -= q;
  lot.costPLN -= part.costPLN;
  lot.costCur -= part.costCur;
  if (lot.qty <= EPS) lot.closedDay = lot.closedDay || null;
  return part;
}

interface TransferGroup {
  outs: Tx[];
  ins: Tx[];
  id?: string;
  done?: boolean;
}

// ------------------------------------------------------------------ model
/** Builds the model data: only values transferable via structured clone (can be done in a Web Worker). */
export function buildModelData(input: ModelInput, opts: ModelOptions = {}): ModelData {
  const { transactions = [], instruments = {}, snapshots = [], market = {} } = input;
  const warnings: string[] = [];
  const taxRate = opts.taxRate ?? 0.19;
  const taxExempt = new Set<string>(opts.taxExempt || []);
  const settlementDays = opts.settlementDays ?? 0;

  // --- transactions
  const txs: Tx[] = transactions.map((t, k) => {
    const ts = Date.parse(t.datetime.length === 10 ? t.datetime + "T12:00:00Z" : t.datetime);
    return {
      ...t, k, ts,
      day: warsawDay(ts),
      qty: num(t.quantity),
      price: num(t.price),
      amt: num(t.amount),
      fee: num(t.fee),
      taxw: num(t.tax),
      costBasis: t.cost_basis === "" || t.cost_basis === undefined ? null : +t.cost_basis,
      priceCcy: t.price_currency || t.currency,
    };
  });
  const typeOrder: Record<string, number> = { DEPOSIT: 0, CASH_IN: 1, TRANSFER_IN: 2, BUY: 3, TRANSFER_OUT: 4, SELL: 5 };
  txs.sort((a, b) => a.ts - b.ts || (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9) || a.k - b.k);

  const today = opts.today || todayStr();
  const firstDay = txs.length ? addDays(txs.reduce((m, t) => (t.day < m ? t.day : m), txs[0].day), -1) : addDays(today, -1);
  const lastDay = txs.length && txs[txs.length - 1].day > today ? txs[txs.length - 1].day : today;
  const N = diffDays(firstDay, lastDay) + 1;
  const days: Day[] = new Array<Day>(N);
  for (let i = 0; i < N; i++) days[i] = addDays(firstDay, i);

  const fxArr: Record<string, Float64Array> = { PLN: new Float64Array(N).fill(1) };
  const priceArr: Record<string, Float64Array> = {};
  const priceCcy: Record<string, string> = {};
  const missingFx = new Set<string>();
  const { idx, fx, fxTax, pPLN } = makeAccessors({ N, firstDay, fxArr, priceArr, priceCcy, missingFx });

  // --- instruments
  const instr: Record<string, Instrument> = {};
  const allSymbols = new Set<string>();
  for (const t of txs) if (t.symbol) allSymbols.add(t.symbol);
  for (const s of Object.keys(market.prices || {})) allSymbols.add(s);
  for (const s of allSymbols) {
    const r = instruments[s] || {};
    const tx = txs.find((t) => t.symbol === s);
    const isin = r.isin || tx?.isin || "";
    instr[s] = {
      symbol: s,
      isin,
      name: r.name || tx?.name || market.prices?.[s]?.name || s,
      assetClass: r.asset_class || tx?.asset_class || "",
      group: r.group || ((r.asset_class || tx?.asset_class) === "BOND" ? "Obligacje skarbowe" : "Inne"),
      yahoo: r.yahoo ?? s,
      regCurrency: r.currency || "",
      key: isin || s,
    };
  }

  // --- exchange rates (PLN per 1 unit)
  const fxSource: Record<string, string> = {};
  for (const [c, v] of Object.entries(market.fx || {})) {
    fxArr[c] = bfillFirst(ffillSeries(v.points, days, firstDay));
    fxSource[c] = v.source;
  }
  const taxDayOf = (day: Day): Day => (settlementDays > 0 ? addBusinessDays(day, settlementDays) : day);

  // --- quotes
  const priceSource: Record<string, string> = {};
  const snapBySym: Record<string, [Day, number, string | null][]> = {};
  for (const s of snapshots) {
    if (s.kind === "POSITION" && s.symbol && s.price && s.date !== null) (snapBySym[s.symbol] ||= []).push([s.date, +s.price, s.currency]);
  }
  for (const s of allSymbols) {
    const m = market.prices?.[s];
    const ccy = (m && m.points?.length && m.currency) || instr[s].regCurrency || txs.find((t) => t.symbol === s)?.priceCcy || "EUR";
    priceCcy[s] = ccy;
    const pts = new Map<Day, number>();
    // lowest priority: transaction prices, then broker snapshots, finally market quotes
    for (const t of txs) if (t.symbol === s && t.price > 0 && t.priceCcy === ccy && (t.type === "BUY" || t.type === "SELL")) pts.set(t.day, t.price);
    for (const [d, p, c] of snapBySym[s] || []) if (c === ccy) pts.set(d, p);
    const mk: Point[] = m && m.points?.length ? m.points : [];
    if (m && mk.length) {
      const mkFirst = mk[0][0];
      const mkLast = mk[mk.length - 1][0];
      // after the first market quote only market points remain (+ newer snapshots)
      for (const d of [...pts.keys()]) if (d >= mkFirst && d <= mkLast) pts.delete(d);
      for (const [d, p] of mk) pts.set(d, p);
      priceSource[s] = m.source || "market";
    } else priceSource[s] = pts.size ? "fallback" : "none";
    const sorted = [...pts.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1));
    priceArr[s] = ffillSeries(sorted, days, firstDay);
  }
  // substitute quotes: another listing of the same ISIN (converted via exchange rates)
  const hasMarket = (o: string): boolean => priceSource[o] !== "fallback" && priceSource[o] !== "none";
  for (const s of allSymbols) {
    const sibs = [...allSymbols].filter((o) => o !== s && instr[o].isin && instr[o].isin === instr[s].isin && hasMarket(o))
      .sort((x, y) => Number(priceCcy[y] === priceCcy[s]) - Number(priceCcy[x] === priceCcy[s]));
    if (!sibs.length) continue;
    const own = priceArr[s];
    if (!hasMarket(s)) {
      // no own quotes: the whole series from another exchange (same ISIN), converted via exchange rates
      const sib = sibs[0];
      const out = new Float64Array(N);
      for (let i = 0; i < N; i++) {
        const v = priceArr[sib][i] * fx(priceCcy[sib], i) / fx(priceCcy[s], i);
        out[i] = isNaN(v) ? own[i] : v;
      }
      priceArr[s] = out;
      priceSource[s] = "sibling:" + sib;
    } else {
      // gaps before the first quote (e.g. shorter history on a given exchange)
      for (let i = 0; i < N && isNaN(own[i]); i++) {
        for (const sib of sibs) {
          const v = priceArr[sib][i] * fx(priceCcy[sib], i) / fx(priceCcy[s], i);
          if (!isNaN(v)) { own[i] = v; break; }
        }
      }
    }
  }

  // --- transfer pairing
  const legInfo = new Map<number, { group: TransferGroup; counterpart: string }>(); // tx.k -> {group, counterpart}
  const groups = new Map<string, TransferGroup>(); // id -> {outs, ins}
  const byTid = new Map<string, TransferGroup>();
  for (const t of txs) {
    if (t.type !== "TRANSFER_IN" && t.type !== "TRANSFER_OUT") continue;
    if (t.transfer_id) {
      let g = byTid.get(t.transfer_id);
      if (!g) { g = { outs: [], ins: [] }; byTid.set(t.transfer_id, g); }
      g[t.type === "TRANSFER_OUT" ? "outs" : "ins"].push(t);
    }
  }
  const unOut: Tx[] = [], unIn: Tx[] = [];
  for (const [id, g] of byTid) {
    if (g.outs.length && g.ins.length) groups.set(id, g);
    else { unOut.push(...g.outs); unIn.push(...g.ins); }
  }
  for (const t of txs) {
    if ((t.type === "TRANSFER_IN" || t.type === "TRANSFER_OUT") && !t.transfer_id) (t.type === "TRANSFER_OUT" ? unOut : unIn).push(t);
  }
  // automatic pairing: same ISIN, same total quantity, different accounts, ±14 days
  const usedOut = new Set<number>();
  for (const tin of unIn) {
    const key = instr[tin.symbol]?.key;
    const cands = unOut.filter((o) => !usedOut.has(o.k) && instr[o.symbol]?.key === key && o.account !== tin.account && Math.abs(diffDays(o.day, tin.day)) <= 14);
    if (!cands.length) continue;
    const byDay: Record<string, Tx[]> = {};
    for (const o of cands) (byDay[o.day + "|" + o.account] ||= []).push(o);
    for (const grp of Object.values(byDay)) {
      const q = grp.reduce((s, o) => s + o.qty, 0);
      if (Math.abs(q - tin.qty) < 1e-6) {
        const id = "auto-" + tin.k;
        groups.set(id, { outs: grp, ins: [tin] });
        grp.forEach((o) => usedOut.add(o.k));
        break;
      }
    }
  }
  for (const [id, g] of groups) {
    const inAcc = g.ins[0].account, outAcc = g.outs[0].account;
    g.id = id;
    g.done = false;
    for (const o of g.outs) legInfo.set(o.k, { group: g, counterpart: inAcc });
    for (const o of g.ins) legInfo.set(o.k, { group: g, counterpart: outAcc });
  }
  // cash transfer pairing
  const cashLegs = txs.filter((t) => t.type === "CASH_IN" || t.type === "CASH_OUT");
  const cashPair = new Map<number, string>();
  const cashByTid: Record<string, Tx[]> = {};
  for (const t of cashLegs) if (t.transfer_id) (cashByTid[t.transfer_id] ||= []).push(t);
  for (const legs of Object.values(cashByTid)) {
    const o = legs.find((x) => x.type === "CASH_OUT"), i = legs.find((x) => x.type === "CASH_IN");
    if (o && i) { cashPair.set(o.k, i.account); cashPair.set(i.k, o.account); }
  }
  for (const o of cashLegs) {
    if (o.type !== "CASH_OUT" || cashPair.has(o.k)) continue;
    const i = cashLegs.find((x) => x.type === "CASH_IN" && !cashPair.has(x.k) && x.account !== o.account && Math.abs(x.ts - o.ts) < 10 * 60000);
    if (i) { cashPair.set(o.k, i.account); cashPair.set(i.k, o.account); }
  }

  // --- main pass
  const accounts = [...new Set(txs.map((t) => t.account))].sort();
  const acc: Record<string, AccountSeries> = {};
  for (const a of accounts) {
    acc[a] = {
      id: a,
      broker: txs.find((t) => t.account === a)?.broker || a.split("-")[0],
      qtyDelta: {}, // symbol -> Float64Array
      cashDelta: {}, // ccy -> Float64Array
      flow: new Float64Array(N), // external flows (PLN)
      openCostDelta: new Float64Array(N),
      symCostDelta: {},
      // daily series — filled in after the main pass
      qty: {},
      sec: new Float64Array(N),
      cash: new Float64Array(N),
      cashCcy: {},
      openCost: new Float64Array(N),
      symCost: {},
    };
  }
  const xferFlows: XferFlow[] = []; // {i, account, counterpart, v}
  const lotsByPos: Record<string, Lot[]> = {}; // account|symbol -> [lots]
  const allLots: Lot[] = [];
  const realized: RealizedEntry[] = [];
  const incomes: IncomeEntry[] = []; // {account, i, day, year, kind, gross, withheld, symbol}
  const taxes: TaxEntry[] = []; // taxes withheld by the broker
  const fees: FeeEntry[] = []; // {account, i, day, v (PLN, positive), kind}
  const purchases: Purchase[] = []; // every purchase (for the purchase profitability analysis)
  const qd = (a: string, s: string): Float64Array => (acc[a].qtyDelta[s] ||= new Float64Array(N));
  const cd = (a: string, c: string): Float64Array => (acc[a].cashDelta[c] ||= new Float64Array(N));
  const posKey = (a: string, s: string): string => a + "|" + s;
  const costDelta = (a: string, s: string, i: number, v: number): void => {
    acc[a].openCostDelta[i] += v;
    (acc[a].symCostDelta[s] ||= new Float64Array(N))[i] += v;
  };
  let lotSeq = 0;

  const removeLots = (t: Tx, qty: number, preferTs?: number | null): { taken: Lot[]; missing: number } => {
    const list = lotsByPos[posKey(t.account, t.symbol)] || [];
    const taken: Lot[] = [];
    let need = qty;
    if (preferTs) {
      const lot = list.find((l) => l.qty > EPS && Math.abs(l.openTs - preferTs) < 120000 && l.qty >= need - EPS);
      if (lot) {
        const q = Math.min(lot.qty, need);
        taken.push(splitLot(lot, q));
        need -= q;
      }
    }
    for (const lot of list) {
      if (need <= EPS) break;
      if (lot.qty <= EPS) continue;
      const q = Math.min(lot.qty, need);
      taken.push(splitLot(lot, q));
      need -= q;
    }
    lotsByPos[posKey(t.account, t.symbol)] = list.filter((l) => l.qty > EPS);
    if (need > 1e-6) warnings.push(`${t.account} ${t.symbol}: sprzedaż/wydanie ${qty} szt. w dniu ${t.day}, a w partiach FIFO brakuje ${+need.toFixed(6)} szt. (niepełna historia?) — brakujący koszt przyjęto jako 0.`);
    return { taken, missing: Math.max(0, need) };
  };
  const addLot = (newLot: Omit<Lot, "id">): Lot => {
    const lot: Lot = Object.assign(newLot, { id: ++lotSeq });
    (lotsByPos[posKey(lot.account, lot.symbol)] ||= []).push(lot);
    lotsByPos[posKey(lot.account, lot.symbol)].sort((a, b) => a.openTs - b.openTs);
    allLots.push(lot);
    return lot;
  };

  const processTransferGroup = (g: TransferGroup): void => {
    g.done = true;
    const moved: Lot[] = [];
    for (const o of g.outs) {
      const i = idx(o.day);
      const { taken } = removeLots(o, o.qty, o.acquired ? Date.parse(o.acquired) : null);
      const c = taken.reduce((s, l) => s + l.costPLN, 0);
      costDelta(o.account, o.symbol, i, -c);
      moved.push(...taken);
    }
    const totalMoved = moved.reduce((s, l) => s + l.qty, 0);
    const totalIn = g.ins.reduce((s, t) => s + t.qty, 0);
    const ratio = totalMoved > EPS ? totalIn / totalMoved : 1;
    for (const tin of g.ins) {
      const share = totalIn > EPS ? tin.qty / totalIn : 1;
      const i = idx(tin.day);
      let cost = 0;
      for (const l of moved) {
        const nl = addLot({
          ...l, account: tin.account, symbol: tin.symbol, qty: l.qty * ratio * share, qty0: l.qty * ratio * share,
          costPLN: l.costPLN * share, costCur: l.costCur * share, transferredFrom: l.account, transferDay: tin.day,
        });
        cost += nl.costPLN;
      }
      costDelta(tin.account, tin.symbol, i, cost);
    }
  };

  for (const t of txs) {
    const a = acc[t.account];
    const i = idx(t.day);
    const taxDay = taxDayOf(t.day);
    const year = +taxDay.slice(0, 4);
    if (t.amt) cd(t.account, t.currency)[i] += t.amt;
    switch (t.type) {
      case "DEPOSIT":
      case "WITHDRAWAL":
        a.flow[i] += t.amt * fx(t.currency, i);
        break;
      case "CASH_IN":
      case "CASH_OUT": {
        const cp = cashPair.get(t.k);
        const v = t.amt * fx(t.currency, i);
        if (cp) xferFlows.push({ i, k: t.k, account: t.account, counterpart: cp, v });
        else a.flow[i] += v;
        break;
      }
      case "BUY": {
        qd(t.account, t.symbol)[i] += t.qty;
        const cost = -t.amt;
        const r = fxTax(t.currency, taxDay);
        const lot = addLot({
          account: t.account, symbol: t.symbol, buySymbol: t.symbol, key: instr[t.symbol].key,
          openTs: t.ts, openDay: t.day, qty: t.qty, qty0: t.qty, costCur: cost, costCcy: t.currency,
          costPLN: cost * r, fxOpen: r, pricePaid: t.price, priceCcy: t.priceCcy, txId: t.id,
        });
        costDelta(t.account, t.symbol, i, lot.costPLN);
        purchases.push({ lot, t, i, costPLN: lot.costPLN, unitPLN: lot.costPLN / t.qty });
        if (t.fee) fees.push({ account: t.account, i, day: t.day, year: +t.day.slice(0, 4), v: t.fee * fx(t.currency, i), kind: "prowizja", symbol: t.symbol });
        break;
      }
      case "SELL": {
        qd(t.account, t.symbol)[i] -= t.qty;
        const r = fxTax(t.currency, taxDay);
        const proceedsPLN = t.amt * r;
        const { taken, missing } = removeLots(t, t.qty);
        let costTotal = 0;
        for (const l of taken) {
          const share = l.qty / t.qty;
          costTotal += l.costPLN;
          realized.push({
            account: t.account, symbol: t.symbol, key: instr[t.symbol].key, qty: l.qty,
            openDay: l.openDay, closeDay: t.day, taxDay, year, i,
            proceedsPLN: proceedsPLN * share, costPLN: l.costPLN, gainPLN: proceedsPLN * share - l.costPLN,
            proceedsCur: t.amt * share, costCur: l.costCur, ccy: t.currency, costCcy: l.costCcy,
            fxSell: r, fxBuy: l.fxOpen, days: diffDays(l.openDay, t.day), txId: t.id, lotId: l.id,
          });
        }
        if (missing > EPS) {
          const share = missing / t.qty;
          realized.push({ account: t.account, symbol: t.symbol, key: instr[t.symbol].key, qty: missing, openDay: null, closeDay: t.day, taxDay, year, i,
            proceedsPLN: proceedsPLN * share, costPLN: 0, gainPLN: proceedsPLN * share, proceedsCur: t.amt * share, costCur: 0, ccy: t.currency, fxSell: r, days: null, txId: t.id, incomplete: true });
        }
        costDelta(t.account, t.symbol, i, -costTotal);
        if (t.fee) fees.push({ account: t.account, i, day: t.day, year: +t.day.slice(0, 4), v: t.fee * fx(t.currency, i), kind: "prowizja", symbol: t.symbol });
        break;
      }
      case "TRANSFER_OUT":
      case "TRANSFER_IN": {
        qd(t.account, t.symbol)[i] += t.type === "TRANSFER_IN" ? t.qty : -t.qty;
        const li = legInfo.get(t.k);
        const v = t.qty * pPLN(t.symbol, i) * (t.type === "TRANSFER_IN" ? 1 : -1);
        if (li) {
          if (!li.group.done) processTransferGroup(li.group);
          xferFlows.push({ i, k: t.k, account: t.account, counterpart: li.counterpart, v: isNaN(v) ? 0 : v });
        } else if (t.type === "TRANSFER_IN") {
          const acqDay = t.acquired ? warsawDay(t.acquired) : t.day;
          let costCur: number | null = t.costBasis;
          const costCcy = t.currency;
          let costPLN: number;
          if (costCur !== null && !isNaN(costCur)) costPLN = costCur * fxTax(costCcy, taxDayOf(acqDay));
          else {
            costPLN = isNaN(v) ? 0 : v;
            costCur = costPLN / fx(t.currency, i);
            warnings.push(`${t.account} ${t.symbol}: przyjęcie ${t.qty} szt. (${t.day}) bez pary i bez cost_basis — koszt nabycia przyjęto jako wartość rynkową.`);
          }
          addLot({ account: t.account, symbol: t.symbol, buySymbol: t.symbol, key: instr[t.symbol].key, openTs: t.acquired ? Date.parse(t.acquired) : t.ts, openDay: acqDay,
            qty: t.qty, qty0: t.qty, costCur, costCcy, costPLN, fxOpen: costPLN / (costCur || 1), pricePaid: costCur / t.qty, priceCcy: costCcy, txId: t.id, external: true });
          costDelta(t.account, t.symbol, i, costPLN);
          a.flow[i] += isNaN(v) ? costPLN : v;
        } else {
          const { taken } = removeLots(t, t.qty, t.acquired ? Date.parse(t.acquired) : null);
          costDelta(t.account, t.symbol, i, -taken.reduce((s, l) => s + l.costPLN, 0));
          a.flow[i] += isNaN(v) ? 0 : v;
        }
        break;
      }
      case "DIVIDEND":
      case "INTEREST": {
        const r = fxTax(t.currency, taxDay);
        incomes.push({ account: t.account, i, day: t.day, year, kind: t.type, symbol: t.symbol, gross: (t.amt + t.taxw) * r, withheld: t.taxw * r, net: t.amt * r, ccy: t.currency, amt: t.amt });
        break;
      }
      case "TAX":
        taxes.push({ account: t.account, i, day: t.day, year, v: -t.amt * fxTax(t.currency, taxDay), symbol: t.symbol });
        break;
      case "FEE":
        fees.push({ account: t.account, i, day: t.day, year: +t.day.slice(0, 4), v: -t.amt * fx(t.currency, i), kind: "opłata", symbol: t.symbol, note: t.note });
        break;
      default:
        break;
    }
  }

  // --- daily series per account
  for (const a of accounts) {
    const A = acc[a];
    for (const [s, d] of Object.entries(A.qtyDelta)) {
      const q = new Float64Array(N);
      let run = 0;
      for (let i = 0; i < N; i++) { run += d[i]; if (Math.abs(run) < 1e-9) run = 0; q[i] = run; }
      A.qty[s] = q;
      let warned = false;
      for (let i = 0; i < N; i++) {
        if (q[i] === 0) continue;
        const p = pPLN(s, i);
        if (isNaN(p)) {
          if (!warned) { warnings.push(`${s}: brak notowań/kursu w dniu ${days[i]} — wycena niepełna.`); warned = true; }
          continue;
        }
        A.sec[i] += q[i] * p;
      }
    }
    for (const [c, d] of Object.entries(A.cashDelta)) {
      const q = new Float64Array(N);
      let run = 0;
      for (let i = 0; i < N; i++) { run += d[i]; q[i] = run; }
      A.cashCcy[c] = q;
      for (let i = 0; i < N; i++) { const r = fx(c, i); if (!isNaN(r)) A.cash[i] += q[i] * r; }
    }
    let run = 0;
    for (let i = 0; i < N; i++) { run += A.openCostDelta[i]; A.openCost[i] = run; }
    for (const [s, d] of Object.entries(A.symCostDelta)) {
      const c = new Float64Array(N);
      let r2 = 0;
      for (let i = 0; i < N; i++) { r2 += d[i]; c[i] = Math.abs(r2) < 1e-7 ? 0 : r2; }
      A.symCost[s] = c;
    }
  }
  if (missingFx.size) warnings.unshift(`Brak kursów walut: ${[...missingFx].join(", ")} — wartości w PLN są niepełne (sprawdź połączenie z NBP).`);

  // --- inflation: GUS monthly price level assigned to the last day of the month (inflation in a given calendar
  //     month = GUS m/m index, in a year = December to December), linear interpolation within the month
  let cpi: Float64Array | null = null, cpiLast: string | null = null;
  const lv = (input.inflation && input.inflation.levels) || [];
  if (lv.length >= 2) {
    const monthEnd = (ym: string): Day => addDays((+ym.slice(5, 7) === 12 ? `${+ym.slice(0, 4) + 1}-01` : `${ym.slice(0, 4)}-${String(+ym.slice(5, 7) + 1).padStart(2, "0")}`) + "-01", -1);
    const pts: [Day, number][] = lv.map(([ym, v]): [Day, number] => [monthEnd(ym), v]);
    const c = new Float64Array(N);
    let j = 0;
    for (let i = 0; i < N; i++) {
      const d = days[i];
      while (j < pts.length - 1 && pts[j + 1][0] <= d) j++;
      if (d <= pts[0][0]) c[i] = pts[0][1];
      else if (j >= pts.length - 1) c[i] = pts[pts.length - 1][1]; // after the last reading: no further inflation (unknown)
      else {
        const t = diffDays(pts[j][0], d) / diffDays(pts[j][0], pts[j + 1][0]);
        c[i] = pts[j][1] + t * (pts[j + 1][1] - pts[j][1]);
      }
    }
    cpi = c;
    cpiLast = lv[lv.length - 1][0];
  }

  return {
    cpi, cpiLast,
    days, N, firstDay, lastDay, today, accounts, acc, instr, txs, allLots, lotsByPos, realized, incomes, taxes, fees,
    purchases, xferFlows, xferByK: new Map(xferFlows.map((x): [number, string] => [x.k, x.counterpart])), priceArr, priceCcy, priceSource, fxArr, fxSource,
    warnings, taxRate, taxExempt, snapshots, missingFx,
  };
}

/** Adds the accessors (idx, fx, fxTax, pPLN) and an empty cache to the model data. Does not copy the series. */
export function hydrateModel(data: ModelData): Model {
  return { ...data, ...makeAccessors(data), cache: new Map<string, AggregateResult>() };
}

export function buildModel(input: ModelInput, opts: ModelOptions = {}): Model {
  return hydrateModel(buildModelData(input, opts));
}

/**
 * Typed array buffers in the model data (deduplicated) — the `transfer` list for postMessage(data, transfer),
 * to pass the series from the worker without copying. After the transfer the sender's data is detached.
 */
export function modelTransferables(data: ModelData): ArrayBuffer[] {
  const out = new Set<ArrayBuffer>();
  const add = (a: Float64Array | null): void => { if (a && a.buffer instanceof ArrayBuffer) out.add(a.buffer); };
  const addAll = (r: Record<string, Float64Array>): void => { for (const a of Object.values(r)) add(a); };
  add(data.cpi);
  addAll(data.priceArr);
  addAll(data.fxArr);
  for (const A of Object.values(data.acc)) {
    add(A.flow); add(A.openCostDelta); add(A.sec); add(A.cash); add(A.openCost);
    addAll(A.qtyDelta); addAll(A.cashDelta); addAll(A.symCostDelta); addAll(A.qty); addAll(A.cashCcy); addAll(A.symCost);
  }
  return [...out];
}

// ------------------------------------------------------------------ aggregation for a set of accounts
export function aggregate(model: Model, accountSet: Iterable<string>, taxEnabled = true): AggregateResult {
  const key = [...accountSet].sort().join(",") + "|" + taxEnabled;
  const hit = model.cache.get(key);
  if (hit) return hit;
  const { N, days, acc, taxRate } = model;
  const S = new Set(accountSet);
  const exempt = model.taxExempt || new Set<string>();
  const isT = (a: string): boolean => S.has(a) && !exempt.has(a); // taxable account (not IKE/IKZE)
  const V = new Float64Array(N), sec = new Float64Array(N), cash = new Float64Array(N), F = new Float64Array(N), openCost = new Float64Array(N);
  const secT = new Float64Array(N), openCostT = new Float64Array(N);
  for (const a of S) {
    const A = acc[a];
    if (!A) continue;
    const t = !exempt.has(a);
    for (let i = 0; i < N; i++) {
      sec[i] += A.sec[i]; cash[i] += A.cash[i]; F[i] += A.flow[i]; openCost[i] += A.openCost[i];
      if (t) { secT[i] += A.sec[i]; openCostT[i] += A.openCost[i]; }
    }
  }
  for (const x of model.xferFlows) if (S.has(x.account) && !S.has(x.counterpart)) F[x.i] += x.v;
  for (let i = 0; i < N; i++) V[i] = sec[i] + cash[i];
  const invested = new Float64Array(N);
  let run = 0;
  for (let i = 0; i < N; i++) { run += F[i]; invested[i] = run; }

  // realised / income / taxes per year (tax year) + year-to-date cumulative
  const years: Record<number, YearStats> = {};
  // unprefixed fields: all selected accounts; tax* fields: only taxable accounts (excluding IKE/IKZE)
  const Y = (y: number): YearStats => (years[y] ||= { year: y, realized: 0, proceeds: 0, cost: 0, incomeGross: 0, withheld: 0, dividends: 0, interest: 0, fees: 0,
    taxRealized: 0, taxProceeds: 0, taxCost: 0, taxIncomeGross: 0, taxWithheld: 0, taxDue: 0 });
  const realizedDay = new Float64Array(N), realizedDayT = new Float64Array(N), incomeDay = new Float64Array(N), withheldDay = new Float64Array(N);
  for (const r of model.realized) if (S.has(r.account)) {
    const y = Y(r.year); y.realized += r.gainPLN; y.proceeds += r.proceedsPLN; y.cost += r.costPLN; realizedDay[r.i] += r.gainPLN;
    if (isT(r.account)) { y.taxRealized += r.gainPLN; y.taxProceeds += r.proceedsPLN; y.taxCost += r.costPLN; realizedDayT[r.i] += r.gainPLN; }
  }
  for (const r of model.incomes) if (S.has(r.account)) {
    const y = Y(r.year); y.incomeGross += r.gross; y.withheld += r.withheld;
    if (r.kind === "DIVIDEND") y.dividends += r.gross; else y.interest += r.gross;
    if (isT(r.account)) { y.taxIncomeGross += r.gross; y.taxWithheld += r.withheld; incomeDay[r.i] += r.gross; withheldDay[r.i] += r.withheld; }
  }
  for (const r of model.taxes) if (S.has(r.account)) {
    const y = Y(r.year); y.withheld += r.v;
    if (isT(r.account)) { y.taxWithheld += r.v; withheldDay[r.i] += r.v; }
  }
  for (const f of model.fees) if (S.has(f.account)) Y(f.year).fees += f.v;
  const taxOfYear = (realizedSum: number, incomeGross: number, withheld: number): number => taxRate * Math.max(0, realizedSum) + Math.max(0, taxRate * incomeGross - withheld);
  for (const y of Object.values(years)) y.taxDue = taxOfYear(y.taxRealized, y.taxIncomeGross, y.taxWithheld);

  // tax liability (realised + deferred on unrealised)
  const L = new Float64Array(N), latent = new Float64Array(N), realizedCum = new Float64Array(N);
  let prevYearsTax = 0, curYear: number | null = null, rY = 0, incY = 0, whY = 0, rc = 0;
  for (let i = 0; i < N; i++) {
    const yr = +days[i].slice(0, 4);
    if (yr !== curYear) {
      if (curYear !== null) prevYearsTax += taxOfYear(rY, incY, whY);
      curYear = yr; rY = 0; incY = 0; whY = 0;
    }
    rY += realizedDayT[i]; incY += incomeDay[i]; whY += withheldDay[i]; rc += realizedDay[i];
    realizedCum[i] = rc;
    if (!taxEnabled) continue;
    const unreal = secT[i] - openCostT[i];
    const withUnreal = taxOfYear(rY + unreal, incY, whY);
    const now = taxOfYear(rY, incY, whY);
    latent[i] = withUnreal - now;
    L[i] = prevYearsTax + withUnreal;
  }

  // TWR (daily chain; flow at the start of the day)
  const twrR = new Float64Array(N), twrRnet = new Float64Array(N);
  for (let i = 1; i < N; i++) {
    const den = V[i - 1] + F[i];
    twrR[i] = den > 1 ? V[i] / den - 1 : 0;
    const vn1 = V[i - 1] - L[i - 1], vn = V[i] - L[i];
    const denN = vn1 + F[i];
    twrRnet[i] = denN > 1 ? vn / denN - 1 : 0;
  }
  const res: AggregateResult = { V, sec, cash, F, invested, openCost, L, latent, realizedCum, years, twrR, twrRnet, accounts: [...S], taxEnabled };
  model.cache.set(key, res);
  return res;
}

// ------------------------------------------------------------------ period metrics
export function periodStats(model: Model, agg: AggregateResult, startDay: Day, endDay: Day): PeriodStats {
  const { days, idx } = model;
  const s0 = Math.max(0, idx(startDay) - 1); // value at the end of the day preceding the period
  const e = idx(endDay);
  const { V, F, L, twrR, twrRnet, sec, openCost, realizedCum } = agg;
  let flows = 0, inflow = 0, outflow = 0, dietzW = 0;
  const span = Math.max(1, e - s0);
  for (let i = s0 + 1; i <= e; i++) {
    flows += F[i];
    if (F[i] > 0) inflow += F[i]; else outflow -= F[i];
    dietzW += F[i] * (e - i) / span;
  }
  const gross = V[e] - V[s0] - flows;
  const net = gross - (L[e] - L[s0]);
  let tw = 1, twn = 1;
  for (let i = s0 + 1; i <= e; i++) { tw *= 1 + twrR[i]; twn *= 1 + twrRnet[i]; }
  const years = (e - s0) / 365;
  const ann = (x: number): number | null => (years >= 1 ? Math.pow(x, 1 / years) - 1 : null);
  const t0 = Date.UTC(+days[s0].slice(0, 4), +days[s0].slice(5, 7) - 1, +days[s0].slice(8, 10));
  const flowList: XirrFlow[] = [];
  if (V[s0] > 0.01) flowList.push({ t: t0, v: -V[s0] });
  for (let i = s0 + 1; i <= e; i++) if (Math.abs(F[i]) > 0.001) flowList.push({ t: t0 + (i - s0) * DAY, v: -F[i] });
  const flowListNet = flowList.map((x, k) => (k === 0 && V[s0] > 0.01 ? { t: x.t, v: -(V[s0] - L[s0]) } : x));
  flowList.push({ t: t0 + (e - s0) * DAY, v: V[e] });
  flowListNet.push({ t: t0 + (e - s0) * DAY, v: V[e] - L[e] });
  const denomDietz = V[s0] + dietzW;
  return {
    s0, e, startValue: V[s0], endValue: V[e], flows, inflow, outflow, gross, net,
    taxChange: L[e] - L[s0], taxEnd: L[e], latentEnd: agg.latent[e],
    twr: tw - 1, twrNet: twn - 1, twrAnn: ann(tw), twrNetAnn: ann(twn),
    xirr: xirr(flowList), xirrNet: xirr(flowListNet),
    dietz: denomDietz > 1 ? gross / denomDietz : null,
    unrealized: sec[e] - openCost[e], realizedInPeriod: realizedCum[e] - realizedCum[s0],
    days: e - s0,
  };
}

// last session: [business day before the last one, last business day <= e]
export function lastSession(model: Pick<ModelData, "days">, e: number): [number, number] {
  const wd = (i: number): boolean => { const x = new Date(model.days[i] + "T12:00:00Z").getUTCDay(); return x !== 0 && x !== 6; };
  let le = e;
  while (le > 0 && !wd(le)) le--;
  let pe = le - 1;
  while (pe > 0 && !wd(pe)) pe--;
  return [Math.max(0, pe), le];
}

// ------------------------------------------------------------------ holdings
type HoldingDraft = Omit<HoldingRow, "symbols" | "unrealized" | "unrealizedPct" | "plPeriod" | "weight" | "avgCost"> & { symbols: Set<string> };

export function holdings(model: Model, accountSet: Iterable<string>, startDay: Day, endDay: Day, opts: HoldingsOptions = {}): HoldingRow[] {
  const { idx, acc, instr, pPLN } = model;
  const S = new Set(accountSet);
  const s0 = Math.max(0, idx(startDay) - 1), e = idx(endDay);
  const [prevDay, lastDay] = lastSession(model, e);
  const rows: Record<string, HoldingDraft> = {};
  const R = (k: string, s: string): HoldingDraft => (rows[k] ||= { key: k, symbols: new Set<string>(), name: instr[s].name, isin: instr[s].isin, group: instr[s].group, assetClass: instr[s].assetClass,
    qty: 0, value: 0, valueStart: 0, dayChange: 0, valueT: 0, costT: 0, cost: 0, flowsPeriod: 0, incomePeriod: 0, realizedTotal: 0, realizedPeriod: 0, income: 0, listings: [] });
  for (const a of S) {
    const A = acc[a];
    if (!A) continue;
    for (const [s, q] of Object.entries(A.qty)) {
      const k = instr[s].key;
      const qe = q[e], qs = q[s0];
      if (qe === 0 && qs === 0 && !opts.includeClosed) continue;
      const r = R(k, s);
      r.symbols.add(s);
      const ve = qe * (pPLN(s, e) || 0), vs = qs * (pPLN(s, s0) || 0);
      r.qty += qe; r.value += ve; r.valueStart += vs;
      if (!model.taxExempt.has(a)) r.valueT += ve;
      r.dayChange += q[prevDay] * ((pPLN(s, lastDay) || 0) - (pPLN(s, prevDay) || 0));
      if (qe !== 0) r.listings.push({ account: a, broker: A.broker, symbol: s, qty: qe, value: ve, price: model.priceArr[s][e], ccy: model.priceCcy[s], source: model.priceSource[s] });
    }
  }
  // tax cost of open lots as of day e
  for (const a of S) {
    const A = acc[a];
    if (!A) continue;
    for (const [s, c] of Object.entries(A.symCost)) {
      const k = instr[s].key;
      if (rows[k]) { rows[k].cost += c[e]; if (!model.taxExempt.has(a)) rows[k].costT += c[e]; }
    }
  }
  // flows in the period per instrument (buys/sells/transfers from outside the set) and income
  for (const t of model.txs) {
    if (!S.has(t.account) || !t.symbol) continue;
    const i = idx(t.day);
    const k = instr[t.symbol].key;
    if (i <= s0 || i > e) continue;
    if (!rows[k]) continue;
    const r = rows[k];
    const f = model.fx(t.currency, i);
    if (t.type === "BUY" || t.type === "SELL") r.flowsPeriod += -t.amt * f;
    else if (t.type === "TRANSFER_IN" || t.type === "TRANSFER_OUT") {
      const cp = model.xferByK.get(t.k);
      const internal = cp && S.has(cp);
      if (!internal) r.flowsPeriod += (t.type === "TRANSFER_IN" ? 1 : -1) * t.qty * (pPLN(t.symbol, i) || 0);
    } else if (t.type === "DIVIDEND" || t.type === "INTEREST") r.incomePeriod += t.amt * f;
  }
  for (const x of model.realized) {
    if (!S.has(x.account)) continue;
    const r = rows[x.key];
    if (!r) continue;
    r.realizedTotal += x.gainPLN;
    if (x.i > s0 && x.i <= e) r.realizedPeriod += x.gainPLN;
  }
  for (const x of model.incomes) if (S.has(x.account) && x.symbol && rows[instr[x.symbol]?.key]) rows[instr[x.symbol].key].income += x.net;
  const total = Object.values(rows).reduce((s, r) => s + r.value, 0);
  return Object.values(rows).map((r): HoldingRow => ({
    ...r,
    symbols: [...r.symbols],
    unrealized: r.value - r.cost,
    unrealizedPct: r.cost > 0 ? r.value / r.cost - 1 : null,
    plPeriod: r.value - r.valueStart - r.flowsPeriod + r.incomePeriod,
    weight: total > 0 ? r.value / total : 0,
    avgCost: r.qty > 0 ? r.cost / r.qty : null,
  }));
}

// ------------------------------------------------------------------ lots and purchase profitability
export const HORIZONS: Horizon[] = [
  { k: "1M", d: 30 }, { k: "3M", d: 91 }, { k: "6M", d: 182 }, { k: "1R", d: 365 }, { k: "2L", d: 730 }, { k: "3L", d: 1095 }, { k: "5L", d: 1826 },
];
export function purchaseAnalysis(model: Model, accountSet: Iterable<string>): PurchaseRow[] {
  const S = new Set(accountSet);
  const { idx, pPLN, N, days } = model;
  const eNow = idx(model.today);
  return model.purchases.filter((p) => S.has(p.t.account)).map((p) => {
    const s = p.t.symbol;
    const i = p.i;
    const hz: Record<string, number | null> = {};
    for (const h of HORIZONS) {
      const j = i + h.d;
      if (j >= N || days[j] > model.today) { hz[h.k] = null; continue; }
      const v = pPLN(s, j);
      hz[h.k] = isNaN(v) ? null : v / p.unitPLN - 1;
    }
    const vNow = pPLN(s, eNow);
    const retNow = isNaN(vNow) ? null : vNow / p.unitPLN - 1;
    const d = eNow - i;
    return {
      id: p.t.id, day: p.t.day, account: p.t.account, symbol: s, key: model.instr[s].key, name: model.instr[s].name,
      qty: p.t.qty, price: p.t.price, priceCcy: p.t.priceCcy, costPLN: p.costPLN, unitPLN: p.unitPLN,
      retNow, annNow: retNow !== null && d >= 30 ? Math.pow(1 + retNow, 365 / d) - 1 : null, days: d, hz,
      openQty: p.lot.qty, // remaining quantity in the original lot (after a transfer the lot lives on as a copy)
    };
  });
}

export function openLots(model: Model, accountSet: Iterable<string>): OpenLotRow[] {
  const S = new Set(accountSet);
  const e = model.idx(model.today);
  const out: OpenLotRow[] = [];
  for (const [pk, lots] of Object.entries(model.lotsByPos)) {
    const [a, s] = pk.split("|");
    if (!S.has(a)) continue;
    for (const l of lots) {
      if (l.qty <= EPS) continue;
      const p = model.pPLN(s, e);
      const value = l.qty * p;
      const d = diffDays(l.openDay, model.today);
      const ret = l.costPLN > 0 ? value / l.costPLN - 1 : null;
      out.push({ ...l, value, ret, days: d, ann: ret !== null && d >= 30 ? Math.pow(1 + ret, 365 / d) - 1 : null, key: model.instr[s].key });
    }
  }
  return out.sort((a, b) => a.openTs - b.openTs);
}

// ------------------------------------------------------------------ risk, drawdowns, months
export function twrIndex(agg: AggregateResult, s0: number, e: number, net = false): Float64Array {
  const r = net ? agg.twrRnet : agg.twrR;
  const out = new Float64Array(e - s0 + 1);
  let x = 1;
  out[0] = 1;
  for (let i = s0 + 1; i <= e; i++) { x *= 1 + r[i]; out[i - s0] = x; }
  return out;
}
export function riskStats(model: Pick<ModelData, "days">, agg: AggregateResult, s0: number, e: number, rf = 0.05, net = false): RiskStats {
  const idxS = twrIndex(agg, s0, e, net);
  let peak = 1, mdd = 0, mddPeak = 0, mddTrough = 0, pk = 0;
  const dd = new Float64Array(idxS.length);
  for (let k = 0; k < idxS.length; k++) {
    if (idxS[k] > peak) { peak = idxS[k]; pk = k; }
    dd[k] = idxS[k] / peak - 1;
    if (dd[k] < mdd) { mdd = dd[k]; mddPeak = pk; mddTrough = k; }
  }
  // volatility from business days (skip weekends, when the valuation is flat)
  const rets: number[] = [];
  const r = net ? agg.twrRnet : agg.twrR;
  for (let i = s0 + 1; i <= e; i++) {
    const wd = new Date(model.days[i] + "T12:00:00Z").getUTCDay();
    if (wd === 0 || wd === 6) continue;
    if (agg.V[i - 1] > 1) rets.push(r[i]);
  }
  const mean = rets.reduce((s, x) => s + x, 0) / (rets.length || 1);
  const sd = Math.sqrt(rets.reduce((s, x) => s + (x - mean) ** 2, 0) / Math.max(1, rets.length - 1));
  const vol = sd * Math.sqrt(252);
  const years = (e - s0) / 365;
  const total = idxS[idxS.length - 1];
  const cagr = years > 0 ? Math.pow(total, 1 / Math.max(years, 1e-9)) - 1 : 0;
  const downs = rets.filter((x) => x < 0);
  const dsd = Math.sqrt(downs.reduce((s, x) => s + x * x, 0) / Math.max(1, rets.length)) * Math.sqrt(252);
  return {
    dd, idx: idxS, maxDrawdown: mdd, mddPeakDay: model.days[s0 + mddPeak], mddTroughDay: model.days[s0 + mddTrough],
    vol, cagr: years >= 1 ? cagr : null, sharpe: vol > 0 && years >= 1 ? (cagr - rf) / vol : null,
    sortino: dsd > 0 && years >= 1 ? (cagr - rf) / dsd : null, bestDay: Math.max(...rets, 0), worstDay: Math.min(...rets, 0),
    positiveDays: rets.length ? rets.filter((x) => x > 0).length / rets.length : null,
  };
}
export function monthlyReturns(model: Pick<ModelData, "days" | "N">, agg: AggregateResult, net = false): Record<string, number> {
  const r = net ? agg.twrRnet : agg.twrR;
  const out: Record<string, { f: number; active: boolean }> = {};
  for (let i = 1; i < model.N; i++) {
    const ym = model.days[i].slice(0, 7);
    if (!out[ym]) out[ym] = { f: 1, active: false };
    out[ym].f *= 1 + r[i];
    if (agg.V[i] > 1) out[ym].active = true;
  }
  const res: Record<string, number> = {};
  for (const [ym, v] of Object.entries(out)) if (v.active) res[ym] = v.f - 1;
  return res;
}
export function yearlyTable(model: Model, agg: AggregateResult, rf?: number): YearRow[] {
  void rf;
  const firstActive = model.days.findIndex((d, i) => agg.V[i] > 1 || agg.F[i] !== 0);
  if (firstActive < 0) return [];
  const y0 = +model.days[firstActive].slice(0, 4), y1 = +model.today.slice(0, 4);
  const out: YearRow[] = [];
  for (let y = y0; y <= y1; y++) {
    const st = periodStats(model, agg, `${y}-01-01`, y === y1 ? model.today : `${y}-12-31`);
    const yy: Partial<YearStats> = agg.years[y] || {};
    out.push({ year: y, ...st, realized: yy.realized || 0, income: yy.incomeGross || 0, fees: yy.fees || 0, taxDue: yy.taxDue || 0 });
  }
  return out;
}

// ------------------------------------------------------------------ benchmark ("what if")
export function benchmarkSeries(model: Model, agg: AggregateResult, symbol: string, s0: number, e: number): BenchmarkResult | null {
  const { pPLN } = model;
  if (!model.priceArr[symbol]) return null;
  let units = 0;
  const out = new Float64Array(e - s0 + 1);
  const firstValid = ((): number => { for (let i = s0; i <= e; i++) if (!isNaN(pPLN(symbol, i))) return i; return -1; })();
  if (firstValid < 0) return null;
  const p0 = pPLN(symbol, firstValid);
  const buy = (i: number, v: number): void => { const p = isNaN(pPLN(symbol, i)) ? p0 : pPLN(symbol, i); units += v / p; };
  if (agg.V[s0] > 0) buy(s0, agg.V[s0]);
  for (let i = s0; i <= e; i++) {
    if (i > s0 && agg.F[i]) buy(i, agg.F[i]);
    const p = isNaN(pPLN(symbol, i)) ? p0 : pPLN(symbol, i);
    out[i - s0] = units * p;
  }
  const priceIdx = new Float64Array(e - s0 + 1);
  for (let i = s0; i <= e; i++) { const p = pPLN(symbol, i); priceIdx[i - s0] = isNaN(p) ? NaN : p / p0; }
  return { value: out, priceIdx };
}

// ------------------------------------------------------------------ allocation and rebalancing
export function allocation(model: Model, hold: readonly HoldingRow[], agg: AggregateResult, e: number, by: string): AllocationRow[] {
  const out: Record<string, number> = {};
  for (const h of hold) {
    if (h.value <= 0) continue;
    if (by === "account" || by === "broker" || by === "currency") {
      for (const l of h.listings) {
        const k = by === "account" ? l.account : by === "broker" ? l.broker : l.ccy;
        out[k] = (out[k] || 0) + l.value;
      }
    } else {
      const k = by === "group" ? h.group : by === "assetClass" ? h.assetClass || "Inne" : h.name;
      out[k] = (out[k] || 0) + h.value;
    }
  }
  const cash = agg.cash[e];
  if (Math.abs(cash) > 1) {
    if (by === "account" || by === "broker" || by === "currency") {
      for (const a of agg.accounts) {
        const A = model.acc[a];
        for (const [c, q] of Object.entries(A.cashCcy)) {
          const v = q[e] * model.fx(c, e);
          if (Math.abs(v) < 0.5) continue;
          const k = by === "account" ? a : by === "broker" ? A.broker : c;
          out[k] = (out[k] || 0) + v;
        }
      }
    } else out["Gotówka"] = (out["Gotówka"] || 0) + cash;
  }
  return Object.entries(out).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
}
export function rebalance(current: readonly AllocationRow[], targets: Readonly<Record<string, number | string>>, extra = 0): RebalanceRow[] {
  // current: [{name, value}], targets: {name: %}. Returns a plan for top-ups (buy only) and a full rebalance.
  const names = new Set([...current.map((c) => c.name), ...Object.keys(targets)]);
  const cur: Record<string, number> = Object.fromEntries(current.map((c) => [c.name, c.value]));
  const tsum = Object.values(targets).reduce((s: number, x) => s + (+x || 0), 0) || 1;
  const total = current.reduce((s, c) => s + c.value, 0) + extra;
  const rows: RebalanceRow[] = [...names].map((n) => {
    const t = (+targets[n] || 0) / tsum;
    const v = cur[n] || 0;
    return { name: n, value: v, target: t, current: total - extra > 0 ? v / (total - extra) : 0, targetValue: t * total, diff: t * total - v, buy: 0 };
  });
  // top-ups only: distribute "extra" proportionally to the shortfalls
  const deficits = rows.filter((r) => r.diff > 0);
  const defSum = deficits.reduce((s, r) => s + r.diff, 0);
  for (const r of rows) r.buy = r.diff > 0 && defSum > 0 ? Math.min(r.diff, (extra * r.diff) / defSum) : 0;
  return rows.sort((a, b) => b.target - a.target || b.value - a.value);
}

// ------------------------------------------------------------------ reconciliation with broker snapshots
export function reconcile(model: Model): ReconcileRow[] {
  const out: ReconcileRow[] = [];
  // rows without a date/account (impossible in files produced by the app: these are the first columns) are skipped
  for (const s of model.snapshots) {
    if (s.date === null || s.account === null) continue;
    const A = model.acc[s.account];
    const i = model.idx(s.date);
    if (s.kind === "POSITION") {
      const q = (s.symbol !== null ? A?.qty[s.symbol]?.[i] : undefined) ?? 0;
      out.push({ ...s, date: s.date, account: s.account, kind: s.kind, computed: q, reported: Number(s.quantity), ok: Math.abs(q - Number(s.quantity)) < 1e-6 });
    } else if (s.kind === "CASH") {
      const q = (s.currency !== null ? A?.cashCcy[s.currency]?.[i] : undefined) ?? 0;
      out.push({ ...s, date: s.date, account: s.account, kind: s.kind, computed: q, reported: Number(s.value), ok: Math.abs(q - Number(s.value)) < 0.02 });
    }
  }
  // computed holdings that the broker does not report in the account's latest snapshot
  const lastByAcc: Record<string, Day> = {};
  for (const s of model.snapshots) {
    if (s.date === null || s.account === null) continue;
    if (!lastByAcc[s.account] || s.date > lastByAcc[s.account]) lastByAcc[s.account] = s.date;
  }
  for (const [a, d] of Object.entries(lastByAcc)) {
    const A = model.acc[a];
    if (!A) continue;
    const i = model.idx(d);
    for (const [sym, q] of Object.entries(A.qty)) {
      if (Math.abs(q[i]) < 1e-9) continue;
      if (!model.snapshots.some((s) => s.account === a && s.date === d && s.symbol === sym)) {
        out.push({ date: d, account: a, kind: "POSITION", symbol: sym, computed: q[i], reported: 0, ok: false, source: "brak w raporcie brokera" });
      }
    }
  }
  return out.sort((a, b) => (a.date < b.date ? 1 : -1));
}

// ------------------------------------------------------------------ real (inflation-adjusted) and periodic returns
export function inflationBetween(model: Pick<ModelData, "cpi">, s0: number, e: number): number | null {
  if (!model.cpi) return null;
  return model.cpi[e] / model.cpi[s0] - 1;
}
// gain above inflation in end-of-period PLN: final value minus capital (initial and contributions) inflation-adjusted to the end of the period
export function realGain(model: Pick<ModelData, "cpi">, agg: AggregateResult, s0: number, e: number): number | null {
  if (!model.cpi) return null;
  const c = model.cpi, V = agg.V, F = agg.F;
  let need = V[s0] * c[e] / c[s0];
  for (let i = s0 + 1; i <= e; i++) if (F[i]) need += F[i] * c[e] / c[i];
  return V[e] - need;
}
export function periodicReturns(model: Model, agg: AggregateResult, gran: Granularity = "month", net = false, range: DayRange | null = null): PeriodicReturn[] {
  const { days, today, idx } = model;
  const first = days.findIndex((d, i) => agg.V[i] > 1 || agg.F[i] !== 0);
  if (first < 0) return [];
  const pad = (n: number): string => String(n).padStart(2, "0");
  const out: PeriodicReturn[] = [];
  let y = +days[first].slice(0, 4), m = +days[first].slice(5, 7);
  if (gran === "quarter") m = Math.floor((m - 1) / 3) * 3 + 1;
  if (gran === "year") m = 1;
  const step = gran === "year" ? 12 : gran === "quarter" ? 3 : 1;
  const r = net ? agg.twrRnet : agg.twrR;
  while (true) {
    const pStart = `${y}-${pad(m)}-01`;
    if (pStart > today || (range && pStart > range.end)) break;
    let ny = y, nm = m + step;
    while (nm > 12) { nm -= 12; ny++; }
    const endDay = addDays(`${ny}-${pad(nm)}-01`, -1);
    if (range && endDay < range.start) { y = ny; m = nm; continue; }
    const start = range && range.start > pStart ? range.start : pStart;
    const limit = range && range.end < today ? range.end : today;
    const end = endDay > limit ? limit : endDay;
    const s0 = Math.max(0, idx(start) - 1), e = idx(end);
    let tw = 1;
    for (let i = s0 + 1; i <= e; i++) tw *= 1 + r[i];
    let flows = 0;
    for (let i = s0 + 1; i <= e; i++) flows += agg.F[i];
    const gross = agg.V[e] - agg.V[s0] - flows;
    const pl = net ? gross - (agg.L[e] - agg.L[s0]) : gross;
    const infl = inflationBetween(model, s0, e);
    const rg = realGain(model, agg, s0, e);
    const label = gran === "year" ? `${y}` : gran === "quarter" ? `Q${Math.floor((m - 1) / 3) + 1} ${y}` : `${["sty", "lut", "mar", "kwi", "maj", "cze", "lip", "sie", "wrz", "paź", "lis", "gru"][m - 1]} ${y}`;
    out.push({
      label, start, end, partial: endDay > today, clipped: start !== pStart || (endDay > end && endDay <= today), s0, e, nominal: tw - 1, inflation: infl,
      // realGain === null (no CPI) counts as 0 — just like `null - x` in the original JS
      real: infl === null ? null : tw / (1 + infl) - 1, pl, realPl: (rg === null ? 0 : rg) - (net ? agg.L[e] - agg.L[s0] : 0),
      startValue: agg.V[s0], endValue: agg.V[e], flows,
      cpiKnown: !model.cpiLast || end.slice(0, 7) <= model.cpiLast,
      firstFrom: pStart < days[first] && start <= days[first] ? days[first] : null,
    });
    y = ny; m = nm;
  }
  return out;
}

export const _internal = { ffillSeries, addBusinessDays };
